import { mediaQueryLarge, onDocumentReady, prefersReducedMotion } from '@theme/utilities';

/**
 * The product gallery's height on mobile follows the slide on show.
 *
 * Showcase's mobile gallery is a Swiper with `autoHeight`: the frame is as
 * tall as the current image. Horizon's slides stretch to the tallest slide;
 * birrot-product.css sets each slide to its own height instead, so the
 * track (the element that scrolls and snaps) stays as tall as the tallest
 * image and `slideshow-container`, which clips it, takes the current
 * image's height. The track is never resized: a resize mid-swipe cuts the
 * momentum short (iOS stops a decelerating scroll view whose frame
 * changes), and a track shorter than a slide let the gallery scroll
 * vertically and kept Horizon's visibility observer, whose root is the
 * track, from seeing a tall slide at all.
 *
 * The height comes from the image's width and height attributes, not from a
 * measurement: off-screen slides may be `content-visibility: hidden`.
 * Delegated from the document, so a gallery the variant morph replaces is
 * still followed (the slideshow re-selects its slide after the morph, which
 * dispatches `slideshow:select`).
 *
 * Prototype additions: on mobile, chevrons and a "01 | 05" counter on the
 * slideshow; on desktop, a "01 / 08" counter beside the image column.
 */
const GALLERY = '.product-information media-gallery slideshow-component';
const GRID = '.product-information media-gallery .media-gallery__grid';

/** Swiper's 300ms, the container's height transition in birrot-product.css. */
const WRAP_DURATION = 300;

/** "01" for 1: the counters' two-digit numbers. */
const pad = (n) => String(n).padStart(2, '0');

const CHEVRON = {
  previous: '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M10.5 2.5 5 8l5.5 5.5" fill="none" stroke="currentColor" stroke-width="1.75"/></svg>',
  next: '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M5.5 2.5 11 8l-5.5 5.5" fill="none" stroke="currentColor" stroke-width="1.75"/></svg>',
};

/**
 * The slides the counter counts: what `slideshow.current` indexes (every
 * slide, a video or model included; not a clone a wrap adds).
 * @param {Element} slideshow
 */
const countedSlides = (slideshow) =>
  /** @type {any} */ (slideshow).slides ??
  [...slideshow.querySelectorAll('slideshow-slide:not([hidden])')].filter((s) => s.firstElementChild);

/**
 * Prototype: once per mobile slideshow, previous and next chevrons (Horizon
 * renders its arrows only for the carousel layout), and the "01 | 05"
 * counter in place of Horizon's "1/5" pill (hidden in birrot-product.css).
 * @param {Element} slideshow - A gallery's <slideshow-component>.
 */
function setUp(slideshow) {
  const container = slideshow.querySelector('slideshow-container');
  // Marked before anything is added: a slideshow with no counter (the dots
  // style) gets its chevrons once too.
  if (!(container instanceof HTMLElement) || container.hasAttribute('data-birrot-gallery')) return;
  container.setAttribute('data-birrot-gallery', '');
  const count = countedSlides(slideshow).length;
  if (count < 2) return;

  for (const [action, label] of [['previous', 'Previous image'], ['next', 'Next image']]) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `birrot-slide-arrow birrot-slide-arrow--${action}`;
    button.setAttribute('aria-label', label);
    button.innerHTML = CHEVRON[action];
    button.addEventListener('click', () => step(slideshow, action));
    container.append(button);
  }

  if (!slideshow.querySelector('.slideshow-controls__counter')) return;
  const counter = document.createElement('span');
  counter.className = 'birrot-slide-counter';
  counter.setAttribute('aria-hidden', 'true');
  counter.dataset.count = pad(count);
  container.append(counter);
}

/**
 * A chevron's step: Horizon's own `next` or `previous`, except past either
 * end, where the gallery wraps with its own animation.
 *
 * Horizon's slideshow is `infinite` and wraps by moving the first slide
 * behind the last (or the last before the first) for the length of a smooth
 * scroll, with a placeholder in its slot. On iOS that reorder sent the
 * scroller one slide back before the scroll began, so next on the last
 * image showed the gallery shuffling backwards.
 * @param {Element} slideshow
 * @param {'next' | 'previous'} action
 */
function step(slideshow, action) {
  const slides = /** @type {any} */ (slideshow).slides;
  const current = /** @type {any} */ (slideshow).current;
  if (!slides || slides.length < 2 || typeof current !== 'number') return;
  const last = slides.length - 1;
  const wraps = action === 'next' ? current >= last : current <= 0;
  if (!wraps || prefersReducedMotion()) {
    /** @type {any} */ (slideshow)[action]();
    return;
  }
  wrap(slideshow, action === 'next' ? 0 : last, action);
}

/** The slideshows mid-wrap: a tap meanwhile is dropped. */
const wrapping = new WeakSet();

/**
 * Wraps from the first slide to the last, or the last to the first, scrolling
 * one slide's width in the chevron's direction: a copy of the target slide
 * is put past the end the scroller is at, the scroller is driven to it frame
 * by frame (an instant scroll a frame: a smooth scroll on iOS can start
 * from a stale position, and a native snap re-targets on a DOM change, so
 * snapping is off for the length of the wrap), and once there the copy is
 * dropped and Horizon's own `select` jumps to the real slide, which sets
 * its index, the slides' `aria-hidden` and the height.
 * @param {Element} slideshow
 * @param {number} index - The target slide's index (0 or the last).
 * @param {'next' | 'previous'} direction
 */
async function wrap(slideshow, index, direction) {
  if (wrapping.has(slideshow)) return;
  const track = slideshow.querySelector('slideshow-slides');
  const slides = /** @type {any} */ (slideshow).slides;
  const from = slides?.[/** @type {any} */ (slideshow).current];
  const to = slides?.[index];
  if (!(track instanceof HTMLElement) || !(from instanceof HTMLElement) || !(to instanceof HTMLElement)) return;

  const clone = /** @type {HTMLElement} */ (to.cloneNode(true));
  // Not a slide of Horizon's: no refs for its component, no timeline of a
  // real slide's, nothing for assistive tech, and painted (Horizon hides
  // the content of a slide not `aria-hidden="false"`).
  for (const el of [clone, ...clone.querySelectorAll('[ref]')]) el.removeAttribute('ref');
  clone.removeAttribute('slide-id');
  clone.style.setProperty('--slideshow-timeline', '--birrot-wrap');
  clone.setAttribute('aria-hidden', 'false');
  clone.setAttribute('data-birrot-wrap', '');
  clone.inert = true;

  wrapping.add(slideshow);
  track.style.scrollSnapType = 'none';
  if (direction === 'next') track.append(clone);
  else track.prepend(clone);

  const padding = parseFloat(getComputedStyle(track).paddingInlineStart) || 0;
  // The slide on show stays where it is (a copy put before it shifts the
  // slides by its width; WebKit shifts the scroll with them, Chromium not).
  const start = from.offsetLeft - padding;
  const end = clone.offsetLeft - padding;
  track.scrollTo({ left: start, behavior: 'instant' });
  // The height and the counter move with the scroll, as Swiper's do.
  fitToSlide(slideshow, to, index);

  await new Promise((resolve) => {
    const t0 = performance.now();
    const tick = (/** @type {number} */ now) => {
      const t = Math.min(1, (now - t0) / WRAP_DURATION);
      const eased = 1 - Math.pow(1 - t, 3);
      track.scrollTo({ left: start + (end - start) * eased, behavior: 'instant' });
      if (t < 1) requestAnimationFrame(tick);
      else resolve(undefined);
    };
    requestAnimationFrame(tick);
  });

  clone.remove();
  wrapping.delete(slideshow);
  // `select` reads the slide's position after the copy is gone, which lays
  // the track out before it scrolls, and dispatches `slideshow:select`.
  /** @type {any} */ (slideshow).select(index, undefined, { animate: false });
  // `select` returns early when its index did not change; the height follows
  // the slide either way.
  fitToSlide(slideshow);
  track.style.scrollSnapType = '';
}

/**
 * The container's height follows the slide on show, and the counter counts it.
 * @param {Element} slideshow - A gallery's <slideshow-component>.
 * @param {Element} [slide] - The slide on show; the slideshow's current one when left out.
 * @param {number} [index] - Its index; the slideshow's current one when left out.
 */
function fitToSlide(slideshow, slide, index) {
  const track = slideshow.querySelector('slideshow-slides');
  const container = slideshow.querySelector('slideshow-container');
  if (!(track instanceof HTMLElement) || !(container instanceof HTMLElement)) return;
  const width = track.clientWidth;

  const current =
    slide ??
    /** @type {any} */ (slideshow).slides?.[/** @type {any} */ (slideshow).current] ??
    // Before the element is upgraded: the slide the markup shows.
    slideshow.querySelector('slideshow-slide[aria-hidden="false"]') ??
    slideshow.querySelector('slideshow-slide');

  const counter = container.querySelector(':scope > .birrot-slide-counter');
  if (counter instanceof HTMLElement) {
    const at = index ?? /** @type {any} */ (slideshow).current ?? 0;
    counter.textContent = `${pad(at + 1)} | ${counter.dataset.count}`;
  }

  const image = current?.querySelector('img[width][height]');
  const ratio = image ? Number(image.getAttribute('width')) / Number(image.getAttribute('height')) : 0;

  if (mediaQueryLarge.matches || !ratio) {
    container.style.removeProperty('height');
    return;
  }

  container.style.height = `${width / ratio}px`;
}

/**
 * Prototype: the desktop gallery's counter ("01 — 08"), held at the
 * viewport's middle on the image column's right edge as the images scroll
 * past. The image crossing the viewport's centre line is the current one.
 * Only for two or more images; decorative (the images are in the page), so
 * hidden from assistive tech. birrot-product.css shows it from 750px, where
 * the grid replaces the slideshow.
 * @param {Element} grid - A gallery's `.media-gallery__grid`.
 */
function addCounter(grid) {
  const gallery = grid.closest('media-gallery');
  const items = [...grid.children];
  if (!gallery || items.length < 2 || gallery.querySelector('.birrot-gallery-counter')) return;

  const counter = document.createElement('div');
  counter.className = 'birrot-gallery-counter';
  counter.setAttribute('aria-hidden', 'true');
  counter.innerHTML =
    '<div class="birrot-gallery-counter__inner">' +
    '<span class="birrot-gallery-counter__current">01</span>' +
    '<span class="birrot-gallery-counter__rule"></span>' +
    `<span>${pad(items.length)}</span>` +
    '</div>';
  gallery.append(counter);

  const current = /** @type {HTMLElement} */ (counter.querySelector('.birrot-gallery-counter__current'));
  items.forEach((item, i) => (/** @type {HTMLElement} */ (item).dataset.birrotIndex = pad(i + 1)));
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) current.textContent = /** @type {HTMLElement} */ (entry.target).dataset.birrotIndex ?? '';
      }
    },
    { rootMargin: '-50% 0px -50% 0px' }
  );
  for (const item of items) observer.observe(item);
}

/** @param {ParentNode} root */
function enhance(root) {
  const slideshows = root instanceof Element && root.matches(GALLERY) ? [root] : root.querySelectorAll(GALLERY);
  for (const slideshow of slideshows) {
    setUp(slideshow);
    fitToSlide(slideshow);
  }
  const grids = root instanceof Element && root.matches(GRID) ? [root] : root.querySelectorAll(GRID);
  for (const grid of grids) addCounter(grid);
}

document.addEventListener('slideshow:select', (event) => {
  const slideshow = event.target instanceof Element ? event.target.closest(GALLERY) : null;
  if (!slideshow) return;

  const { detail } = /** @type {{ detail?: { slide?: Element, index?: number } }} */ (event);
  fitToSlide(slideshow, detail?.slide, detail?.index);
});

// A variant change replaces the whole gallery (media-gallery.js). The new
// slideshow announces its first slide only when that slide is not the
// first one, so a gallery that opens on its first image would keep no
// height: set up and fit every gallery that arrives.
new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (node instanceof Element) enhance(node);
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });

function refit() {
  for (const slideshow of document.querySelectorAll(GALLERY)) fitToSlide(slideshow);
}

mediaQueryLarge.addEventListener('change', refit);
window.addEventListener('resize', refit);

onDocumentReady(() => enhance(document));
